import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { UserStore } from './userStore';
import { CompanyService } from '/src/api';
import { Company, TCompanyId } from '/src/lib/models';
import {
  CompanyStatus,
  CreateCompanyForm,
  MenuItemType,
  Region,
  TBaseQuery,
  TCreateCompanyRequest,
  UserRole,
} from '/src/lib/types';

export interface CompanyStoreProps {
  /**
   * List of Companies
   */
  companies?: Company[];
  /**
   * Currently selected Company
   */
  currentCompany?: Company;
}

export class CompanyStore extends BaseStore implements CompanyStoreProps {
  private didInitMenu = false;
  userStore: UserStore;

  companies?: Company[];
  currentCompany?: Company;

  constructor(rootStore: RootStore) {
    super(rootStore);

    this.userStore = rootStore.userStore;

    makeObservable(this, {
      companies: observable,
      currentCompany: observable,
    });
  }

  /**
   * Adds Company nav menu items to the app header.
   */
  initMenuItems = () => {
    if (this.didInitMenu || !this.rootStore.userStore.user) return;

    // Add menu items
    this.rootStore.menuStore.addMenuItem(
      'topbar',
      'Companies',
      'companies',
      !this.rootStore.userStore.isAdmin ? MenuItemType.Item : MenuItemType.Dropdown,
      '/companies(/create)?',
      false,
      undefined,
      7
    );
    if (this.rootStore.userStore.isAdmin) {
      this.rootStore.menuStore.addSubMenuItem('topbar', 'companies', 'List Companies', 'companies');
      this.rootStore.menuStore.addSubMenuItem(
        'topbar',
        'companies',
        'New Company',
        'companies/create',
        'companies/create',
        false,
        [UserRole.Admin]
      );
    }

    runInAction(() => (this.didInitMenu = true));
  };

  /**** Data Processing ****/

  /**
   * Formats List Companies responses.
   *
   * @param companies - An array of Companies to format
   * @returns The formatted Companies
   */
  formatCompanyList = (companies: Company[]) => {
    return companies.map((company) => {
      // Parse dates
      company.statementDate = new Date(company.statementDate);

      // Add tags
      // TODO: do we need this part, or is this only for the filters?
      company.tags = {
        companies: [company._id],
        loans: company.loans?.map((loan) => loan._id) ?? [],
        lenders: [],
      };

      return company;
    });
  };

  /**
   * Format Get Company responses.
   *
   * @param company - The Company to format
   * @returns The formatted Company
   */
  formatCompany = (company: Company) => {
    // Calculate
    company.currentAvailableCredit = company.currentCredit - company.currentPrincipalBalances;

    // Return
    return company;
  };

  /**** Company CRUD ****/

  /**
   * Fetches a list of Companies.
   *
   * @param query - Optional query to send with the API request
   * @returns An array of Companies
   */
  query = async (query?: TBaseQuery) => {
    if (query) query.px = true;
    const response = await CompanyService.query(query);

    runInAction(() => {
      this.companies = this.formatCompanyList(response);
    });

    return this.companies;
  };

  /**
   * Fetches a single Company and sets it as the current Company.
   *
   * @param companyId - ID of the Company to fetch
   * @returns A Company object
   */
  get = async (companyId: TCompanyId) => {
    const response = await CompanyService.get(companyId);

    runInAction(() => {
      this.currentCompany = response ? this.formatCompany(response) : undefined;
    });

    return this.currentCompany;
  };

  /**
   * Creates an Company.
   *
   * @param formValues - A populated CreateCompanyForm object
   * @returns The new Company
   */
  create = async (formValues: CreateCompanyForm) => {
    if (!this.userStore.user) throw new Error('Not authorized');

    if (Number.isNaN(parseInt(formValues.zip ?? '')) || !formValues.socketLineOfBusiness) {
      throw new Error('Please fix the errors first.');
    }

    // Create new Company object
    const company: TCreateCompanyRequest = {
      name: formValues.name,
      longName: formValues.longName,
      address: {
        street: formValues.street,
        city: formValues.city,
        state: formValues.state as Region,
        zip: formValues.zip,
      },
      contactPerson: formValues.contactPerson,
      phone: {
        primary: formValues.phonePrimary,
        secondary: formValues.phoneSecondary,
        fax: formValues.phoneFax,
      },
      email: formValues.email,
      website: formValues.website,
      socketLineOfBusiness: formValues.socketLineOfBusiness,
      companyType: formValues.companyType,
      notes: formValues.notes,
      files: formValues.files,
      isLender: formValues.isLender,
      status: CompanyStatus.Active,
      user: this.userStore.user._id,
    };

    const response = await CompanyService.create(company);

    return response;
  };

  /**
   * Updates an Company.
   *
   * @param company - The Company to update
   * @returns The updated Company
   */
  update = async (company: Company) => {
    if (!this.userStore.user) throw new Error('Not authorized');

    // if () throw new Error('Please fix the errors first.');

    // TODO: this is the logic in the original code; set company.loans, then delete it

    const loanIds = company.loans.map((loan) => loan._id);
    company.loans = loanIds;

    delete company.adjustments;
    delete company.advances;
    delete company.companies;
    delete company.payments;
    delete company.loans;
    delete company.statements;

    const response = await CompanyService.update(company._id, company);

    runInAction(() => (this.currentCompany = Object.assign(this.currentCompany ?? {}, response)));

    return this.currentCompany;
  };

  /**
   * Deactivates a Company. This is equivalent to a soft delete.
   *
   * @param companyId - ID of the Company to deactivate
   * @returns The deactivated Company
   */
  deactivateCompany = async (companyId: TCompanyId) => {
    const response = await CompanyService.remove(companyId);

    return response;
  };
}
